import axios from "axios";
import i18n from "../i18n/index.ts";

export const API_URL = "https://happycap.eu/api";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// ✅ Interceptor para agregar token de acceso a cada request
api.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem("access");
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  config.headers["Accept-Language"] = i18n.language || "fr";

  return config;
});

// 🔁 Interceptor para intentar refrescar token automáticamente
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      localStorage.getItem("refresh")
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem("refresh");
        const res = await axios.post(`${API_URL}/token/refresh/`, {
          refresh: refreshToken,
        });

        const newAccess = res.data.access;
        localStorage.setItem("access", newAccess);
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;

        return api(originalRequest);
      } catch (err) {
        localStorage.clear();
        window.location.href = "/login";
        return Promise.reject(err);
      }
    }

    if (error.response?.status === 401 && !localStorage.getItem("refresh")) {
      localStorage.clear();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

// =======================
// ✅ FUNCIONES DE API
// =======================

// 🔐 Auth
export const registerUser = (data) =>
  api.post("/register/", data, { withCredentials: true });

export const activateAccount = (code) =>
  api.post("/activate/", { code }, { withCredentials: true });

export const loginUser = (email, password) =>
  api.post("/login/", { email, password }, { withCredentials: true });

// 👤 Perfil
export const getProfile = () => api.get("/profile/", { withCredentials: true });
export const updateProfile = (formData) =>
  api.put("/profile/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
export const changePassword = (currentPassword, newPassword) =>
  api.post("/change-password/", {
    current_password: currentPassword,
    new_password: newPassword,
  }, { withCredentials: true });

// 📬 Recursos con filtros (nuevo)
export const getAsociaciones = (params = {}) =>
  api.get("/recursos/asociaciones/", { params });

export const getProyectos = (params = {}) =>
  api.get("/recursos/proyectos/", { params });

export const getCentros = (params = {}) =>
  api.get("/recursos/centros/", { params });

// 📰 Posts
export const getPosts = (params = {}) =>
  api.get("/posts/", { params });
export const createPost = (formData) =>
  api.post("/posts/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
export const editPost = (id, formData) =>
  api.put(`/posts/${id}/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
export const deletePost = (id) => api.delete(`/posts/${id}/`, { withCredentials: true });
export const likePost = (postId) => api.post(`/posts/${postId}/like/`, { withCredentials: true });
export const editComment = (commentId, text) =>
  api.patch(`/posts/comments/${commentId}/`, { text }, { withCredentials: true });

export const deleteComment = (commentId) =>
  api.delete(`/posts/comments/${commentId}/`, { withCredentials: true });

export const addComment = (postId, text) =>
  api.post(`/posts/${postId}/comment/`, { text }, { withCredentials: true });
export const searchPosts = (params = {}) =>
  api.get("/posts/search/", { params });
// 📸 Galería
export const getImagePosts = () => api.get("/gallery/images/");
export const createImagePost = (formData) =>
  api.post("/gallery/images/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
export const updateImagePost = (id, formData) =>
  api.patch(`/gallery/images/${id}/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
export const deleteImagePost = (id) => api.delete(`/gallery/images/${id}/`, { withCredentials: true });
export const likeImage = (imageId) =>
  api.post(`/gallery/images/${imageId}/like/`, { withCredentials: true });
export const commentImage = (imageId, text) =>
  api.post(`/gallery/images/${imageId}/comment/`, { text }, { withCredentials: true });
export const editImageComment = (commentId, text) =>
  api.patch(`/gallery/comments/${commentId}/`, { text }, { withCredentials: true });

export const deleteImageComment = (commentId) =>
  api.delete(`/gallery/comments/${commentId}/`, { withCredentials: true });

export const getImageComments = (imageId) =>
  api.get(`/gallery/images/${imageId}/comments/`);

// 🔍 Buscador
export const buscarPorPerfil = (params) =>
  api.get("/buscar/perfil/", { params });
export const buscarPorEnfermedad = (orphacode) =>
  api.get("/buscar/enfermedad/", {
    params: { orphacode },
  });
export const getPublicProfile = (id) =>
  api.get(`/buscar/perfil/${id}/`, {
    params: {
      t: Date.now(),
    },
  });

// 👥 Amistades
export const enviarSolicitudAmistad = (profileId) =>
  api.post(`/solicitudes/enviar/${profileId}/`, { withCredentials: true });
export const cancelarAmistad = (profileId) =>
  api.delete(`/solicitudes/cancelar/${profileId}/`, { withCredentials: true });
export const getNotificacionesAmistad = () =>
  api.get("/notificaciones/amistad/");
export const getSolicitudesRecibidas = () =>
  api.get("/solicitudes/");
export const aceptarSolicitud = (id) =>
  api.post(`/solicitudes/${id}/aceptar/`, { withCredentials: true });
export const rechazarSolicitud = (id) =>
  api.delete(`/solicitudes/${id}/rechazar/`, { withCredentials: true });

// 💬 Chat
export const getChatFriends = () => api.get("/chat/friends/");

export const getChatHistory = (friendId) =>
  api.get(`/chat/historial/${friendId}/`);

export const marcarMensajesLeidos = (senderId) =>
  api.post(`/chat/marcar-leidos/${senderId}/`, { withCredentials: true });

export const getUnreadMessages = () =>
  api.get("/chat/no-leidos/");
// 🛑 Mensajes bloqueados (solo admin)
export const getMensajesBloqueados = () =>
  api.get("/chat/bloqueados/");

export const denunciarMensajeBloqueado = (id) =>
  api.post(`/chat/bloqueados/${id}/denunciar/`, { withCredentials: true });

export const eliminarMensajesExpirados = () =>
  api.delete("/chat/bloqueados/eliminar-expirados/", { withCredentials: true });


export const deleteProfile = async () => {
  try {
    const res = await api.delete("/delete-account/", { withCredentials: true });
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("email");

    window.location.href = "/login";
    return res.data;
  } catch (err) {
    console.error("Error al eliminar la cuenta:", err);
    throw err;
  }
};

export default api;
